import { FunctionComponent, useContext, useState, useCallback, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import classNames from 'classnames';
import { useCheckoutSelector, useAppDispatch } from '../reducers/root';
import { getCheckoutParam } from '../helpers/checkoutRedirects';
import { PANES } from '../constants/checkoutConstants';
import { CheckoutServerVarsContext } from '../components/shared/CheckoutServerVarsContext';
import CheckoutRestrictionAcknowledgement from './CheckoutRestrictionAcknowledgement';
import CheckoutSubmitButton from '../components/containers/CheckoutSubmitButton';
import CheckoutBuyerMessage from './CheckoutBuyerMessage';
import { CheckoutAddressView } from '../shared/CheckoutAddressView';
import CheckoutGifting from './CheckoutGifting';
import { CheckoutReviewStepDeliveryView } from './CheckoutReviewStepDeliveryView';
import CheckoutPaymentView from '../payment/CheckoutPaymentView';
import CheckoutReviewStepDetailsContainer from './CheckoutReviewStepDetailsContainer';
import CheckoutReviewStepChargesDisclaimer from './CheckoutReviewStepChargesDisclaimer';
import CheckoutTaxExemptionCheckbox from '../components/shared/CheckoutTaxExemptionCheckbox';
import CheckoutVatReverseChargeCheckbox from '../components/shared/CheckoutVatReverseChargeCheckbox';
import CheckoutShippingAndDeliveryError from '../components/shared/CheckoutShippingAndDeliveryError';
import CheckoutDeliveryOptions from '../delivery/CheckoutDeliveryOptions';
import { PreloadedDuties } from '../delivery/usePreloadedDuties';
import { shipmentQuoteSelectedAction } from '../actions/commonActions';
import { shipmentNotesErrorAction } from '../actions/checkoutDeliveryActions';
import { trackUserInteractionOnce } from '../helpers/checkoutTracking';
import { useDisableShippingSubmit } from '../delivery/useDisableShippingSubmit';
import { useUpdateShipmentQuote } from '../delivery/useUpdateShipmentQuote';

import { CheckoutReviewStep_viewer$key } from './__generated__/CheckoutReviewStep_viewer.graphql';
import { CheckoutReviewStep_item$key } from './__generated__/CheckoutReviewStep_item.graphql';
import { CheckoutReviewStep_buyer$key } from './__generated__/CheckoutReviewStep_buyer.graphql';
import { CheckoutReviewStep_transaction$key } from './__generated__/CheckoutReviewStep_transaction.graphql';
import { CheckoutReviewStep_deliveryOptions$key } from './__generated__/CheckoutReviewStep_deliveryOptions.graphql';
import { CheckoutReviewStep_preCheckoutTransaction$key } from './__generated__/CheckoutReviewStep_preCheckoutTransaction.graphql';

import styles from './styles/CheckoutReviewStep.scss';

const viewerFragment = graphql`
    fragment CheckoutReviewStep_viewer on Viewer {
        ...CheckoutSubmitButton_viewer
        ...CheckoutMobileSummaryNewPane_viewer
        ...CheckoutTaxExemptionCheckbox_viewer
        ...CheckoutVatReverseChargeCheckbox_viewer
        isGiftingForAllVerticalsEnabled: featureFlag(feature: "giftingAllVerticals")
    }
`;

const itemFragment = graphql`
    fragment CheckoutReviewStep_item on Item {
        ...CheckoutSubmitButton_item
        ...CheckoutBuyerMessage_item
        ...CheckoutReviewStepDeliveryView_item
        ...CheckoutMobileSummaryNewPane_item
        ...CheckoutDeliveryOptions_item
        ...CheckoutGifting_item
        vertical
    }
`;

const buyerFragment = graphql`
    fragment CheckoutReviewStep_buyer on User {
        ...CheckoutSubmitButton_buyer
        ...CheckoutPaymentView_buyer
        ...CheckoutReviewStepDetailsContainer_buyer
        ...CheckoutMobileSummaryNewPane_buyer
        ...CheckoutDeliveryOptions_buyer
        isTrade: isVerifiedTrade
        addressBook {
            addressList {
                serviceId
                ...CheckoutAddressView_address
            }
        }
    }
`;

const transactionFragment = graphql`
    fragment CheckoutReviewStep_transaction on Transaction {
        ...CheckoutReviewStepChargesDisclaimer_transaction
        ...CheckoutRestrictionAcknowledgement_transaction
        ...CheckoutSubmitButton_transaction
        ...CheckoutReviewStepDeliveryView_transaction
        ...CheckoutTaxExemptionCheckbox_transaction
        ...CheckoutMobileSummaryNewPane_transaction
        ...CheckoutGifting_transaction
        ...CheckoutVatReverseChargeCheckbox_transaction
        ...CheckoutDeliveryOptions_transaction
        ...useUpdateShipmentQuote_transaction
        isGiftEligible
    }
`;

const deliveryOptionsFragment = graphql`
    fragment CheckoutReviewStep_deliveryOptions on ShipmentQuote @relay(plural: true) {
        ...CheckoutReviewStepDeliveryView_deliveryOptions
        ...CheckoutDeliveryOptions_deliveryOptions
        ...useDisableShippingSubmit_deliveryOptions
    }
`;

const preCheckoutTransactionFragment = graphql`
    fragment CheckoutReviewStep_preCheckoutTransaction on Transaction {
        ...CheckoutDeliveryOptions_preCheckoutTransaction
    }
`;

type Props = {
    isComputing: boolean;
    viewer: CheckoutReviewStep_viewer$key;
    item: CheckoutReviewStep_item$key;
    buyer: CheckoutReviewStep_buyer$key;
    transaction: CheckoutReviewStep_transaction$key;
    deliveryOptions: CheckoutReviewStep_deliveryOptions$key;
    preCheckoutTransaction: CheckoutReviewStep_preCheckoutTransaction$key | null;
    preloadedDuties: PreloadedDuties;
};

export const CheckoutReviewStep: FunctionComponent<Props> = props => {
    const { isComputing, preloadedDuties } = props;

    const viewer = useFragment(viewerFragment, props.viewer);
    const item = useFragment(itemFragment, props.item);
    const buyer = useFragment(buyerFragment, props.buyer);
    const transaction = useFragment(transactionFragment, props.transaction);
    const deliveryOptions = useFragment(deliveryOptionsFragment, props.deliveryOptions);
    const preCheckoutTransaction = useFragment(
        preCheckoutTransactionFragment,
        props.preCheckoutTransaction
    );
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [hasShipmentQuoteError, setShipmentQuoteError] = useState(false);

    const shippingAddressId = useCheckoutSelector<string | undefined>(
        state => state.transaction.shippingAddressId
    );
    const shipmentType = useCheckoutSelector(state => state.transaction.shipmentType);
    const shipmentQuoteId = useCheckoutSelector(state => state.transaction.shipmentQuoteId);
    const quantity = useCheckoutSelector(state => state.transaction.quantity);
    const isReturningBuyer = useCheckoutSelector(state => state.pane.isReturningBuyer);
    const shipmentNotes = useCheckoutSelector(state => state.transaction.shipmentNotes);
    const isCustomQuoteSelected = useCheckoutSelector(
        state => state.delivery.isCustomQuoteSelected
    );
    const hasShippingNotesError = useCheckoutSelector(state => state.ui.hasShippingNotesError);
    const isProcessingOffer = useCheckoutSelector(state => state.ui.isProcessingOffer);

    const { isMobile } = useContext(CheckoutServerVarsContext);

    const hasMakeOfferMessage = !!getCheckoutParam('message');

    const shippingAddress =
        !!shippingAddressId &&
        (buyer?.addressBook?.addressList || []).find(
            address => address?.serviceId === shippingAddressId
        );

    const isTradeBuyer = buyer?.isTrade;

    const isJewelrySeller = item?.vertical === 'jewelry';
    const isGiftingEnabled = isJewelrySeller || viewer?.isGiftingForAllVerticalsEnabled;
    const isGiftEligible = transaction?.isGiftEligible;

    const isShippingDisabled = useDisableShippingSubmit({ deliveryOptions, isLoading });
    const [updateShipmentQuote] = useUpdateShipmentQuote({ transaction });

    const handleDeliveryOptionSelect = useCallback(
        // remove TSFixMe when `CheckoutDeliveryOptions` is typed
        async (options: $TSFixMe) => {
            setIsLoading(true);
            trackUserInteractionOnce();
            let hasError = false;
            const { shipmentType: newShipmentType, shipmentQuoteId: newShipmentQuoteId } = options;
            const quoteUpdateResult = await updateShipmentQuote({
                newShipmentType,
                newShipmentQuoteId,
            });
            hasError = quoteUpdateResult.hasError;
            if (!hasError) {
                dispatch(
                    shipmentQuoteSelectedAction({
                        ...options,
                        isCustomQuoteSelected: options.isCustomQuoteSelected || false,
                    })
                );
            }

            setShipmentQuoteError(hasError);
            setIsLoading(false);
        },
        [dispatch, updateShipmentQuote]
    );

    useEffect(() => {
        if (isReturningBuyer && isCustomQuoteSelected && !shipmentNotes) {
            dispatch(shipmentNotesErrorAction());
        }
    }, [isCustomQuoteSelected, shipmentNotes, dispatch, isReturningBuyer]);

    return (
        <div className={styles.reviewPaneSpacing}>
            <div
                className={classNames(styles.reviewDetailsContainer, {
                    [styles.reviewDetailsContainerBottomBorder]: !isGiftEligible,
                })}
            >
                {shippingAddress && (
                    <CheckoutReviewStepDetailsContainer
                        buyer={buyer}
                        pane={PANES.PANE_SHIPPING}
                        dataTn="change-shipping-address"
                    >
                        <CheckoutAddressView address={shippingAddress} />
                    </CheckoutReviewStepDetailsContainer>
                )}
                {!isReturningBuyer && (
                    <CheckoutReviewStepDetailsContainer
                        buyer={buyer}
                        pane={PANES.PANE_DELIVERY}
                        dataTn="change-shipping-method"
                    >
                        <CheckoutReviewStepDeliveryView
                            item={item}
                            deliveryOptions={deliveryOptions}
                            transaction={transaction}
                        />
                    </CheckoutReviewStepDetailsContainer>
                )}
                <CheckoutReviewStepDetailsContainer
                    buyer={buyer}
                    pane={PANES.PANE_PAYMENT}
                    dataTn="change-payment-method"
                >
                    <CheckoutPaymentView buyer={buyer} />
                </CheckoutReviewStepDetailsContainer>
                {isReturningBuyer && (
                    <CheckoutReviewStepDetailsContainer
                        buyer={buyer}
                        pane={PANES.PANE_DELIVERY}
                        dataTn="change-shipping-method"
                        disableChangeLink
                    >
                        <>
                            {(hasShippingNotesError || hasShipmentQuoteError) && (
                                <CheckoutShippingAndDeliveryError />
                            )}
                            <CheckoutDeliveryOptions
                                {...{
                                    item,
                                    transaction,
                                    buyer,
                                    preCheckoutTransaction,
                                    deliveryOptions,
                                    shipmentType,
                                    shipmentQuoteId,
                                    quantity,
                                    viewer,
                                    preloadedDuties,
                                }}
                                isComputing={isComputing || isLoading || isProcessingOffer}
                                onSelect={handleDeliveryOptionSelect}
                            />
                        </>
                    </CheckoutReviewStepDetailsContainer>
                )}
            </div>
            {isGiftingEnabled && (
                <CheckoutGifting
                    isMobile={isMobile}
                    isComputing={isComputing}
                    item={item}
                    transaction={transaction}
                />
            )}
            {hasMakeOfferMessage && <CheckoutBuyerMessage item={item} />}
            <CheckoutRestrictionAcknowledgement transaction={transaction} />
            {isMobile && (
                <CheckoutTaxExemptionCheckbox
                    isMobile
                    isComputing={isComputing}
                    viewer={viewer}
                    transaction={transaction}
                />
            )}
            {isMobile && isTradeBuyer && (
                <CheckoutVatReverseChargeCheckbox
                    viewer={viewer}
                    transaction={transaction}
                    isComputing={isComputing}
                />
            )}
            <CheckoutReviewStepChargesDisclaimer transaction={transaction} />
            <div className={styles.submitCta}>
                <CheckoutSubmitButton
                    viewer={viewer}
                    item={item}
                    buyer={buyer}
                    transaction={transaction}
                    isComputing={isComputing}
                    isLoading={isLoading}
                    isDisabled={isShippingDisabled}
                />
            </div>
        </div>
    );
};
